import { useTranslations } from '@vocab/react';
import {
  ButtonLink,
  IconNewWindow,
  IconRocket,
  IconTick,
  Stack,
  Text,
} from 'braid-design-system';
import type { FC } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { CheckReferencesActionOrigins } from 'src/constants/pageTrackingData';
import { buildWhatAreSeekReferenceCheckArticleLink } from 'src/constants/urls';
import { TrackOnMount } from 'src/libs/Analytics';
import {
  DotPointItem,
  type ImageModalProps,
  ImageModal,
  ImageModalContent,
} from 'src/libs/ImageModal';

import { PurchaseAndUpgradeReferenceCheckImage } from '../PurchaseAndUpgradeReferenceCheckImage';

import translations from './.vocab';

interface PurchaseReferenceCheckAddOnDialogProps {
  actionOrigin: CheckReferencesActionOrigins;
  jobId: number;
  onMount: () => void;
  onClickPurchase: () => void;
  onClose: ImageModalProps['close'];
  open: ImageModalProps['isOpen'];
}

export const PurchaseReferenceCheckAddOnDialog: FC<
  PurchaseReferenceCheckAddOnDialogProps
> = ({ jobId, onClose, open, actionOrigin, onMount, onClickPurchase }) => {
  const titleId = 'PurchaseReferenceCheckAddOnDialog';

  const { t } = useTranslations(translations);
  const { router, site, language } = useAppConfig();

  const purchaseLink = router.purchaseReferenceCheck({
    jobId,
    ref: actionOrigin,
  });

  const readMoreLink = buildWhatAreSeekReferenceCheckArticleLink({
    site,
    language,
  });

  return (
    <ImageModal
      isOpen={open}
      close={onClose}
      image={<PurchaseAndUpgradeReferenceCheckImage />}
      titleId={titleId}
    >
      <ImageModalContent
        titleId={titleId}
        heading={t("Easily check an applicant's references")}
        actions={
          <>
            <ButtonLink
              href={purchaseLink}
              icon={<IconRocket />}
              iconPosition="trailing"
              size="small"
              target="_self"
              tone="brandAccent"
              onClick={onClickPurchase}
            >
              {t('Buy reference checks')}
            </ButtonLink>
            {readMoreLink ? (
              <ButtonLink
                href={readMoreLink}
                icon={<IconNewWindow />}
                iconPosition="trailing"
                size="small"
                target="_blank"
                tone="neutral"
                variant="soft"
              >
                {t('Read more')}
              </ButtonLink>
            ) : null}
          </>
        }
        content={
          <TrackOnMount onMount={onMount}>
            <Stack space="medium">
              <DotPointItem
                icon={<IconTick tone="positive" />}
                content={
                  <Text size="small">
                    {t('Check references for up to 3 applicants.')}
                  </Text>
                }
              />
              <DotPointItem
                icon={<IconTick tone="positive" />}
                content={
                  <Text size="small">
                    {t(
                      "We'll send each applicant's nominated referees standard questions.",
                    )}
                  </Text>
                }
              />
              <DotPointItem
                icon={<IconTick tone="positive" />}
                content={
                  <Text size="small">
                    {t(
                      "You'll receive their full responses in a standard report.",
                    )}
                  </Text>
                }
              />
            </Stack>
          </TrackOnMount>
        }
      />
    </ImageModal>
  );
};
